import { useTranslation } from "react-i18next";
import { LatLngLiteral } from "leaflet";
import * as Yup from "yup";
import { useMemo, useState, useCallback } from "react";
import { useToast } from "@chakra-ui/react";
import { useStores } from "../../../stores/setup/use-store";
import { CoordinatesType } from "../../../pages/Location/types/";

interface FormValues {
  name: string;
}

export function useSaveGeofenceModal(
  selectedGeofence?: LatLngLiteral[][] | LatLngLiteral[],
  setFieldValue?: (field: string, value: any, shouldValidate?: boolean) => void
) {
  const { t } = useTranslation("translation", {
    keyPrefix: "oceanWeatherIntelligence.insightAlert",
  });
  const toast = useToast();
  const {
    dataStores: { locationStore, insightAlertStore },
  } = useStores();
  const [isSaving, setIsSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const normalizedGeofence = useMemo((): LatLngLiteral[] => {
    if (!selectedGeofence || selectedGeofence.length === 0) {
      return [];
    }
    return Array.isArray(selectedGeofence[0])
      ? (selectedGeofence as LatLngLiteral[][]).flat()
      : (selectedGeofence as LatLngLiteral[]);
  }, [selectedGeofence]);

  const initialValues: FormValues = useMemo(
    () => ({
      name: "",
    }),
    []
  );

  const validationSchema = useMemo(
    () =>
      Yup.object({
        name: Yup.string().required(t("requiredField") as string),
      }),
    [t]
  );

  const handleOpenModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleSave = useCallback(
    async (values: FormValues, onSuccess?: () => void) => {
      if (!normalizedGeofence || normalizedGeofence.length === 0) {
        toast({
          title: t("pleaseDrawAnArea"),
          status: "warning",
          duration: 3000,
          isClosable: true,
          position: "bottom-right",
        });
        return;
      }

      setIsSaving(true);
      try {
        const locationData = {
          name: values.name,
          coordinatesType: CoordinatesType.AREA,
          coordinates: normalizedGeofence,
        };

        const { id } = await locationStore.createLocationAPI(locationData);
        const formValues = insightAlertStore.createFormValues;
        if (formValues) {
          insightAlertStore.setCreateFormValues({
            ...formValues,
            geofence: undefined,
            locationId: id,
          });
        }

        setFieldValue?.("locationId", id);
        setFieldValue?.("geofence", undefined);

        toast({
          title: t("areaSuccessOnSave"),
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "bottom-right",
        });

        insightAlertStore.setIsGeofenceSavedToLocation(true);

        if (onSuccess) {
          onSuccess();
        }
      } catch (error: any) {
        if (error.response?.status === 409 || error.response?.status === 406) {
          toast({
            title: t("areaNameAlreadyExists"),
            status: "error",
            duration: 5000,
            isClosable: true,
            position: "bottom-right",
          });
        } else {
          toast({
            title: t("errorCreatingArea"),
            status: "error",
            duration: 5000,
            isClosable: true,
            position: "bottom-right",
          });
        }
      } finally {
        setIsSaving(false);
      }
    },
    [normalizedGeofence, locationStore, toast, t, insightAlertStore, setFieldValue]
  );

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      await handleSave(values, handleCloseModal);
    },
    [handleSave, handleCloseModal]
  );

  const resetForm = () => {
    return initialValues;
  };

  return {
    t,
    initialValues,
    validationSchema,
    resetForm,
    handleSave,
    isSaving,
    isSaved: insightAlertStore.isGeofenceSavedToLocation,
    isModalOpen,
    handleOpenModal,
    handleCloseModal,
    handleSubmit,
    normalizedGeofence,
  };
}
